/**
 * The desk's four views (`APP-016`).
 *
 * `contracts/design/app-desk/` turns seven stacked sections into four tabs. The canvas draws
 * them as anchors, not as buttons, and that is the part worth preserving: a founder can link
 * someone to their auction, open a tab in a new window, and use the back button, none of
 * which a `useState` tab strip gives them.
 *
 * So they are real paths, served by the SPA fallback already configured in `wrangler.jsonc`,
 * and {@link useDeskTab} upgrades an in-app click to `history.pushState` so switching views
 * does not refetch the desk. A founder with JavaScript disabled still gets a working link;
 * the shell simply loads again.
 *
 * Modelled on `dealbid-web`'s `src/lib/routes.ts` rather than on a router: `AGENTS.md` § 5
 * prefers simple inspectable primitives, and § 16 keeps a library migration out of a task
 * that was not scoped for one.
 */

/** The four views, in the order the canvas's tab row draws them. */
export const DESK_TABS = ["deals", "auction", "results", "how-it-works"] as const;

/** One of {@link DESK_TABS}. */
export type DeskTab = (typeof DESK_TABS)[number];

/** The default view. Canon `D-008` makes submission the first thing a new founder needs. */
export const DEFAULT_TAB: DeskTab = "deals";

/** The path each view answers to. The default view owns the root. */
export const TAB_PATHS: Record<DeskTab, string> = {
  deals: "/",
  auction: "/auction",
  results: "/results",
  "how-it-works": "/how-it-works",
};

/**
 * What the tab is called.
 *
 * `short` is what the canvas draws at 390, where `Tomorrow's auction` does not fit a strip of
 * four. It is a shorter label for the same view, never a different one — the accessible name
 * a screen reader announces is the full label at both widths.
 */
export const TAB_LABELS: Record<DeskTab, { full: string; short: string }> = {
  deals: { full: "Your deals", short: "Your deals" },
  auction: { full: "Tomorrow's auction", short: "Auction" },
  results: { full: "Results", short: "Results" },
  "how-it-works": { full: "How it works", short: "How it works" },
};

/**
 * Longest path first, so a nested path can never be claimed by its prefix. `/` is excluded
 * because it is the fallback rather than a match.
 */
const MATCHABLE: readonly (readonly [DeskTab, string])[] = DESK_TABS.filter(
  (tab) => TAB_PATHS[tab] !== "/",
)
  .map((tab) => [tab, TAB_PATHS[tab]] as const)
  .sort((a, b) => b[1].length - a[1].length);

/**
 * The view a pathname names, tolerating a trailing slash and any casing.
 *
 * An unrecognised path falls back to the default view rather than to a 404. That is the same
 * position `dealbid-web` takes and it is forced by the same deployment: the SPA fallback
 * serves this shell for every path, so there is no path at which a 404 could be produced.
 */
export function tabForPath(pathname: string): DeskTab {
  const normalized = pathname.replace(/\/+$/, "").toLowerCase();
  if (normalized === "") return DEFAULT_TAB;
  const match = MATCHABLE.find(([, path]) => path === normalized);
  return match ? match[0] : DEFAULT_TAB;
}

/** The path that names a view. */
export function pathForTab(tab: DeskTab): string {
  return TAB_PATHS[tab];
}
